import { gridEventsByDate } from '@/utils/calendar/appointment-grid-events'
import { toIsoDate } from '@/utils/calendar/date'
import { resolveCalendarEventType } from '@/utils/calendar/event-types'
import {
  CalendarEventSourceEnum,
  CalendarEventsService,
  CasesService,
  UsersService,
  type CalendarEvent,
  type CalendarEventTypeEnum,
  type CustomerCaseList,
  type TUser
} from '@/types/generated'
import { computed, ref, watch, type Ref } from 'vue'
import type { CalendarGridEvent } from '@/types/calendar/grid'
import {
  normalizeExternalParticipantDetails,
  participantDisplayLabel
} from '@/types/calendar/participants'

const PAGE_SIZE = 200
const MIN_EVENT_DURATION_MIN = 15

interface UseCalendarEventsOptions {
  untitledLabel: Ref<string>
  selectedCalendarUuid: Ref<string | null>
  visibleRangeStart: Ref<string | null>
  visibleRangeEnd: Ref<string | null>
  activeFilterTypes: Ref<Set<CalendarEventTypeEnum>>
  canEditCalendar: (calendarUuid: string) => boolean
  participantInvitesSupported: Ref<boolean>
}

type CalendarCaseLookup = Pick<CustomerCaseList, 'case_title'> & {
  uuid: string
  jupus_case_number?: string | null
}

function userDisplayName(user?: Pick<TUser, 'first_name' | 'last_name' | 'email'> | null): string {
  if (!user) {
    return ''
  }

  const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
  return name || user.email || ''
}

function buildGridEvent(
  event: CalendarEvent,
  untitledLabel: string,
  usersByUuid: Record<string, TUser>,
  casesByUuid: Record<string, CalendarCaseLookup>,
  canEditCalendar: (calendarUuid: string) => boolean,
  participantInvitesSupported: boolean
): CalendarGridEvent | null {
  const start = new Date(event.start)
  if (Number.isNaN(start.getTime())) {
    return null
  }

  const end = new Date(event.end)
  const endTime = Number.isNaN(end.getTime())
    ? start.getTime() + MIN_EVENT_DURATION_MIN * 60_000
    : end.getTime()
  const durationMin = Math.max(
    MIN_EVENT_DURATION_MIN,
    Math.round((endTime - start.getTime()) / 60_000)
  )
  const owner = usersByUuid[event.owner]
  const assistants = (event.assistants ?? [])
    .map((uuid) => userDisplayName(usersByUuid[uuid]))
    .filter(Boolean)
  const participantsInternal = (event.participants_internal ?? [])
    .map((uuid) => userDisplayName(usersByUuid[uuid]))
    .filter(Boolean)
  const participantsExternalDetails = normalizeExternalParticipantDetails(
    event.participants_external
  )
  const participantsExternal = participantsExternalDetails.map(
    (participant) => participantDisplayLabel(participant) || participant.email
  )
  const caseItem = event.case ? casesByUuid[event.case] : undefined

  return {
    uuid: event.uuid,
    date: toIsoDate(start),
    hour: start.getHours(),
    startMin: start.getHours() * 60 + start.getMinutes(),
    durationMin,
    start: event.start,
    end: event.end,
    allDay: Boolean(event.all_day),
    title: event.title?.trim() || untitledLabel,
    subtitle: event.location_text?.trim() || undefined,
    locationText: event.location_text?.trim() || undefined,
    description: event.description?.trim() || undefined,
    wegen: event.wegen?.trim() || undefined,
    keywords: [...(event.keywords ?? [])],
    tone: 'primary',
    type: resolveCalendarEventType(event),
    calendarId: event.calendar,
    caseUuid: event.case ?? null,
    caseNumber: caseItem?.jupus_case_number ?? null,
    caseTitle: caseItem?.case_title ?? null,
    ownerUuid: event.owner,
    ownerName: userDisplayName(owner) || undefined,
    assistantUuids: [...(event.assistants ?? [])],
    assistants,
    participantInternalUuids: [...(event.participants_internal ?? [])],
    participantsInternal,
    participants: [...participantsInternal, ...participantsExternal],
    participantsExternal,
    participantsExternalDetails,
    participantInvitesSupported,
    privacy: event.privacy,
    source: event.source ?? CalendarEventSourceEnum.JUPUS,
    canEdit: canEditCalendar(event.calendar)
  }
}

async function fetchCalendarEvents(params: {
  calendarUuid: string
  visibleRangeStart: string
  visibleRangeEnd: string
}): Promise<CalendarEvent[]> {
  const results: CalendarEvent[] = []
  let offset = 0
  let hasMore = true

  while (hasMore) {
    const response = await CalendarEventsService.calendarEventsList({
      calendar: params.calendarUuid,
      startAfter: params.visibleRangeStart,
      endBefore: params.visibleRangeEnd,
      limit: PAGE_SIZE,
      offset
    })
    const page = response.results ?? []
    results.push(...page)

    if (page.length < PAGE_SIZE) {
      hasMore = false
    } else {
      offset += page.length
    }
  }

  return results
}

async function fetchUser(uuid: string): Promise<TUser | null> {
  try {
    return await UsersService.usersRetrieve({ uuid })
  } catch {
    return null
  }
}

async function fetchCase(uuid: string): Promise<CalendarCaseLookup | null> {
  try {
    const response = await CasesService.casesRetrieve({ uuid })
    if (!response.uuid) {
      return null
    }

    return {
      uuid: response.uuid,
      jupus_case_number:
        'jupus_case_number' in response ? (response.jupus_case_number ?? null) : null,
      case_title: 'case_title' in response ? (response.case_title ?? null) : null
    }
  } catch {
    return null
  }
}

function uniqueNonEmpty(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))]
}

async function loadLookupsForEvents(events: CalendarEvent[]): Promise<{
  usersByUuid: Record<string, TUser>
  casesByUuid: Record<string, CalendarCaseLookup>
}> {
  const userUuids = uniqueNonEmpty(
    events.flatMap((event) => [
      event.owner,
      ...(event.assistants ?? []),
      ...(event.participants_internal ?? [])
    ])
  )
  const caseUuids = uniqueNonEmpty(events.map((event) => event.case))

  const [users, cases] = await Promise.all([
    Promise.all(userUuids.map((uuid) => fetchUser(uuid))),
    Promise.all(caseUuids.map((uuid) => fetchCase(uuid)))
  ])

  return {
    usersByUuid: Object.fromEntries(
      users.filter((user): user is TUser => Boolean(user)).map((user) => [user.uuid, user])
    ),
    casesByUuid: Object.fromEntries(
      cases
        .filter((caseItem): caseItem is CalendarCaseLookup => Boolean(caseItem))
        .map((caseItem) => [caseItem.uuid, caseItem])
    )
  }
}

export function useCalendarEvents(options: UseCalendarEventsOptions) {
  const events = ref<CalendarEvent[]>([])
  const isLoading = ref(false)
  const loadError = ref<string | null>(null)
  const usersByUuid = ref<Record<string, TUser>>({})
  const casesByUuid = ref<Record<string, CalendarCaseLookup>>({})
  let loadRequestId = 0

  const loadEvents = async (): Promise<void> => {
    const calendarUuid = options.selectedCalendarUuid.value
    const visibleRangeStart = options.visibleRangeStart.value
    const visibleRangeEnd = options.visibleRangeEnd.value

    if (!calendarUuid || !visibleRangeStart || !visibleRangeEnd) {
      events.value = []
      usersByUuid.value = {}
      casesByUuid.value = {}
      loadError.value = null
      return
    }

    const requestId = ++loadRequestId
    isLoading.value = true
    loadError.value = null

    try {
      const calendarEvents = await fetchCalendarEvents({
        calendarUuid,
        visibleRangeStart,
        visibleRangeEnd
      })
      const lookups = await loadLookupsForEvents(calendarEvents)

      if (requestId !== loadRequestId) {
        return
      }

      events.value = calendarEvents
      usersByUuid.value = lookups.usersByUuid
      casesByUuid.value = lookups.casesByUuid
    } catch (error) {
      if (requestId !== loadRequestId) {
        return
      }
      console.error('[calendar] loadEvents failed', error)
      loadError.value = 'load_failed'
    } finally {
      if (requestId === loadRequestId) {
        isLoading.value = false
      }
    }
  }

  watch(
    [options.selectedCalendarUuid, options.visibleRangeStart, options.visibleRangeEnd],
    () => {
      void loadEvents()
    },
    { immediate: true }
  )

  const focusedEvents = computed(() => {
    if (!options.selectedCalendarUuid.value) {
      return []
    }

    return events.value.filter((event) => event.calendar === options.selectedCalendarUuid.value)
  })

  const focusedGridEvents = computed(() =>
    focusedEvents.value.flatMap((event) => {
      const gridEvent = buildGridEvent(
        event,
        options.untitledLabel.value,
        usersByUuid.value,
        casesByUuid.value,
        options.canEditCalendar,
        options.participantInvitesSupported.value
      )
      if (!gridEvent) {
        return []
      }

      return [gridEvent]
    })
  )

  const filteredGridEvents = computed(() =>
    focusedGridEvents.value.flatMap((gridEvent) => {
      if (!options.activeFilterTypes.value.has(gridEvent.type)) {
        return []
      }

      return [gridEvent]
    })
  )

  const gridEventsByUuid = computed(() => {
    const map = new Map<string, CalendarGridEvent>()
    for (const event of focusedGridEvents.value) {
      map.set(event.uuid, event)
    }
    return map
  })

  const eventsByDate = computed(() => gridEventsByDate(filteredGridEvents.value))

  const eventDates = computed(() => {
    const dates = new Set<string>()
    for (const event of filteredGridEvents.value) {
      dates.add(event.date)
    }
    return dates
  })

  const appendEvent = async (event: CalendarEvent): Promise<void> => {
    try {
      const lookups = await loadLookupsForEvents([event])
      usersByUuid.value = {
        ...usersByUuid.value,
        ...lookups.usersByUuid
      }
      casesByUuid.value = {
        ...casesByUuid.value,
        ...lookups.casesByUuid
      }
    } catch (error) {
      console.error('[calendar] appendEvent lookups failed', error)
    }

    events.value = [event, ...events.value.filter((item) => item.uuid !== event.uuid)]
  }

  const removeEvent = (uuid: string): void => {
    events.value = events.value.filter((item) => item.uuid !== uuid)
  }

  return {
    events,
    focusedEvents,
    gridEventsByUuid,
    gridEvents: filteredGridEvents,
    eventsByDate,
    eventDates,
    isLoading,
    loadError,
    loadEvents,
    appendEvent,
    removeEvent
  }
}
